import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

const DEFAULT_TIMEOUT_MS = 15000;
const DEFAULT_MAXIMUM_AGE_MS = 0;
const FAST_TIMEOUT_MS = 2000;
const ACCURATE_TIMEOUT_MS = 12000;

const isNative = () => Capacitor.isNativePlatform();

function normalizePositionOptions(options = {}) {
  const timeout = options.timeout ?? DEFAULT_TIMEOUT_MS;
  const maximumAge = options.maximumAge ?? DEFAULT_MAXIMUM_AGE_MS;
  const enableHighAccuracy = options.enableHighAccuracy !== false;
  const navigatorTimeout = Math.min(8000, Math.max(1000, timeout - 2000));
  return { timeout, maximumAge, enableHighAccuracy, navigatorTimeout };
}

function wrapNavigatorTimeout(promise, timeoutMs) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(Object.assign(new Error('TIMEOUT'), { code: 3 })), timeoutMs);
    promise
      .then((v) => { clearTimeout(timer); resolve(v); })
      .catch((e) => { clearTimeout(timer); reject(e); });
  });
}

async function getCurrentPositionNative(options) {
  const { timeout, maximumAge, enableHighAccuracy } = normalizePositionOptions(options);
  const position = await Geolocation.getCurrentPosition({
    enableHighAccuracy,
    timeout,
    maximumAge,
  });
  return position;
}

async function getCurrentPositionWeb(options) {
  const { timeout, maximumAge, enableHighAccuracy, navigatorTimeout } = normalizePositionOptions(options);
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw Object.assign(new Error('UNSUPPORTED'), { code: 0 });
  }
  const position = await wrapNavigatorTimeout(
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy,
        timeout: navigatorTimeout,
        maximumAge,
      });
    }),
    timeout
  );
  return position;
}

/** Whether geolocation is available on this platform. */
export function isGeolocationSupported() {
  if (isNative()) return true;
  return typeof navigator !== 'undefined' && !!navigator.geolocation;
}

/**
 * Platform-aware position read. Native uses @capacitor/geolocation (app-branded iOS prompt).
 * Web uses navigator.geolocation.
 * @returns {Promise<{ coords: { latitude, longitude, accuracy? }, timestamp? }>}
 */
export async function getCurrentPosition(options = {}) {
  if (isNative()) return getCurrentPositionNative(options);
  return getCurrentPositionWeb(options);
}

/**
 * Coarse / cellular-friendly fix for instant UI (onboarding transition, quick map seed).
 * Native: enableHighAccuracy false. maximumAge helps on web only.
 */
export async function getCurrentPositionFast(options = {}) {
  return getCurrentPosition({
    enableHighAccuracy: false,
    timeout: options.timeout ?? FAST_TIMEOUT_MS,
    maximumAge: options.maximumAge ?? 120000,
    ...options,
  });
}

/**
 * High-accuracy GPS lock for background refinement after fast fix.
 */
export async function getCurrentPositionAccurate(options = {}) {
  return getCurrentPosition({
    enableHighAccuracy: true,
    timeout: options.timeout ?? ACCURATE_TIMEOUT_MS,
    maximumAge: options.maximumAge ?? 0,
    ...options,
  });
}

/**
 * Convenience wrapper returning flat lat/lng/accuracy (onboarding, maps, profiles).
 */
export async function getCurrentPositionWithAccuracy(options = {}) {
  const position = await getCurrentPosition(options);
  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracy: position.coords.accuracy ?? null,
  };
}

/**
 * Flat lat/lng from a fast coarse read.
 */
export async function getCurrentPositionFastWithAccuracy(options = {}) {
  const position = await getCurrentPositionFast(options);
  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracy: position.coords.accuracy ?? null,
  };
}

/**
 * Silent-guard: true only when location permission is already granted.
 */
export async function isLocationPermissionGranted() {
  try {
    const status = await Geolocation.checkPermissions();
    return status.location === 'granted';
  } catch {
    return false;
  }
}

/**
 * Map web DOM codes and native Capacitor GLOC codes to locationStatus values.
 * @returns {'timeout'|'denied'|'error'}
 */
export function mapGeolocationErrorToStatus(err) {
  const code = String(err?.code ?? '');
  const message = String(err?.message ?? '');

  if (
    message === 'TIMEOUT' ||
    code === 3 ||
    code.includes('GLOC-0010')
  ) {
    return 'timeout';
  }

  if (
    code === 1 ||
    code.includes('GLOC-0003') ||
    code.includes('GLOC-0008')
  ) {
    return 'denied';
  }

  return 'error';
}

/** User-facing Arabic hint from a geolocation error (maps, booking, etc.). */
export function getGeolocationErrorMessage(err) {
  const status = mapGeolocationErrorToStatus(err);
  if (status === 'timeout') {
    return 'انتهت مهلة تحديد الموقع. يرجى المحاولة مرة أخرى أو تحديد الموقع يدوياً على الخريطة.';
  }
  if (status === 'denied') {
    return 'لم يتم السماح بتحديد الموقع. يمكنك تحديد الموقع يدوياً على الخريطة.';
  }
  return 'فشل تحديد موقعك. يرجى تحديد الموقع يدوياً على الخريطة.';
}
